import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Printer, Calculator, TrendingUp, Save as SaveIcon } from "lucide-react";
import { toast } from "sonner";
import {
    Tooltip, ResponsiveContainer, Legend, Cell,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    LineChart, Line,
} from "recharts";

const CHART_COLORS = ["#002FA7", "#E63946", "#264653", "#9C27B0", "#FFB703", "#2A9D8F"];

export default function BusinessProposal() {
    const { partId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [editVolumes, setEditVolumes] = useState([]);
    const [savingVol, setSavingVol] = useState(false);

    const loadProposal = () => {
        api.get(`/parts/${partId}/proposal`)
            .then((r) => {
                setData(r.data);
                // initialise editable yearly volumes
                const py = r.data.totals.project_years || 1;
                const existing = r.data.part.yearly_volumes || [];
                const av = r.data.part.annual_volume || 0;
                const arr = Array.from({ length: py }, (_, i) => existing[i] ?? av);
                setEditVolumes(arr);
            })
            .catch((e) => setErr(e.response?.data?.detail || "Failed to load"));
    };

    useEffect(() => { loadProposal(); /* eslint-disable-next-line */ }, [partId]);

    const saveYearlyVolumes = async () => {
        if (!data) return;
        setSavingVol(true);
        try {
            const part = data.part;
            const payload = {
                part_name: part.part_name,
                part_number: part.part_number || "",
                customer_name: part.customer_name || "",
                annual_volume: part.annual_volume || 0,
                target_price: part.target_price || 0,
                photo_path: part.photo_path || "",
                segment: part.segment || "",
                status: part.status || "Lead",
                selected_draft_id: part.selected_draft_id || "",
                project_years: part.project_years || 5,
                yearly_volumes: editVolumes.map((v) => Number(v) || 0),
                notes: part.notes || "",
            };
            await api.put(`/parts/${partId}`, payload);
            toast.success("Yearly volumes saved");
            loadProposal();
        } catch {
            toast.error("Save failed");
        } finally { setSavingVol(false); }
    };

    const resetYearlyToAnnual = () => {
        if (!data) return;
        const py = data.totals.project_years || 1;
        const av = data.part.annual_volume || 0;
        setEditVolumes(Array.from({ length: py }, () => av));
    };

    if (err) return (
        <div className="p-8">
            <p className="text-sm text-zinc-500">{err}</p>
            <Button variant="outline" className="rounded-none mt-4" onClick={() => navigate(`/parts/${partId}/costing`)}>
                Go to Costing → create / select a draft first
            </Button>
        </div>
    );
    if (!data) return <div className="p-8 text-zinc-500">Loading…</div>;

    const { part, totals, figures, projection, draft_name } = data;
    const derivedPrice = figures.derived_price;
    const targetPrice = figures.target_price;
    const grossMarginPct = derivedPrice > 0 ? (figures.profit_per_piece / derivedPrice) * 100 : 0;
    const targetGap = targetPrice - derivedPrice;

    const breakup = [
        { name: "Material", value: totals.material_total, color: "#002FA7" },
        { name: "Process", value: totals.process_total, color: "#E63946" },
        { name: "Outsource", value: totals.outsource_total, color: "#264653" },
        { name: "Investment", value: totals.amortised_investment_per_piece, color: "#9C27B0" },
        { name: "Overhead", value: totals.overhead_fixed, color: "#FFB703" },
        { name: "Profit", value: totals.profit_value, color: "#2A9D8F" },
    ].filter((x) => x.value > 0);

    const waterfallData = (() => {
        let running = 0;
        const out = breakup.map((s) => {
            const item = { name: s.name, base: running, value: s.value, color: s.color };
            running += s.value;
            return item;
        });
        out.push({ name: "Price", base: 0, value: running, color: "#09090b", isTotal: true });
        return out;
    })();

    const annualData = [
        { name: "Annual Revenue", value: figures.annual_revenue },
        { name: "Annual Cost", value: figures.annual_cost },
        { name: "Annual Profit", value: figures.annual_profit },
    ];

    return (
        <div>
            <PageHeader
                eyebrow={`Part No. ${part.part_number || "—"}`}
                title={`Business Proposal — ${part.part_name}`}
                subtitle={`Customer: ${part.customer_name || "—"}  ·  Draft: ${draft_name}  ·  Segment: ${part.segment || "—"}  ·  Project life: ${totals.project_years} yrs`}
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" className="rounded-none" onClick={() => navigate(`/parts/${partId}/costing`)}>
                            <Calculator size={14} className="mr-2" /> Costing
                        </Button>
                        <Button variant="outline" className="rounded-none" onClick={() => navigate(`/parts/${partId}/roi`)}>
                            <TrendingUp size={14} className="mr-2" /> ROI
                        </Button>
                        <Button variant="outline" className="rounded-none" onClick={() => window.open(`${API}/parts/${partId}/proposal/export`, "_blank")} data-testid="export-proposal-excel">
                            <SaveIcon size={14} className="mr-2" /> Export Excel
                        </Button>
                        <Button variant="outline" className="rounded-none" onClick={() => window.print()} data-testid="print-proposal">
                            <Printer size={14} className="mr-2" /> Print
                        </Button>
                        <Button variant="outline" className="rounded-none" onClick={() => navigate("/dashboard")}>
                            <ArrowLeft size={14} className="mr-2" /> Parts
                        </Button>
                    </div>
                }
            />

            <div className="px-8 py-6 space-y-6">
                {/* Hero */}
                <div className="border-2 border-[#09090b] p-6 grid grid-cols-4 gap-6" data-testid="proposal-hero">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Derived Price / piece</div>
                        <div className="text-4xl font-bold font-mono text-[#002FA7]">₹ {derivedPrice.toFixed(2)}</div>
                        <div className="mt-2 text-[11px] text-zinc-500 font-mono">Margin: {grossMarginPct.toFixed(1)}%</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Customer Target</div>
                        <div className="text-2xl font-semibold font-mono">₹ {targetPrice.toFixed(2)}</div>
                        <div className={`mt-2 text-[11px] font-mono ${targetGap >= 0 ? "text-[#2A9D8F]" : "text-[#E63946]"}`} data-testid="target-gap">
                            {targetGap >= 0 ? "Below target by" : "Above target by"} ₹ {Math.abs(targetGap).toFixed(2)}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Annual Volume</div>
                        <div className="text-3xl font-bold font-mono">{(part.annual_volume || 0).toLocaleString()}</div>
                        <div className="mt-2 text-[11px] text-zinc-500 font-mono">Annual Revenue: ₹ {figures.annual_revenue.toLocaleString()}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Annual Profit</div>
                        <div className={`text-3xl font-bold font-mono ${figures.annual_profit >= 0 ? "text-[#2A9D8F]" : "text-[#E63946]"}`} data-testid="annual-profit">
                            ₹ {figures.annual_profit.toLocaleString()}
                        </div>
                        <div className="mt-2 text-[11px] text-zinc-500 font-mono">Profit / piece: ₹ {figures.profit_per_piece.toFixed(2)}</div>
                    </div>
                </div>

                {/* Photo + cost buildup */}
                <div className="grid grid-cols-3 gap-6">
                    <div className="border border-zinc-200 p-6">
                        <div className="tag-pill mb-3">Part</div>
                        {part.photo_path ? (
                            <img src={`${API}/files/${part.photo_path}`} alt={part.part_name} className="w-full aspect-square object-cover border border-zinc-200" />
                        ) : (
                            <div className="w-full aspect-square border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400 font-mono text-xs">NO PHOTO</div>
                        )}
                        <div className="mt-3 space-y-1 text-sm">
                            <div><span className="text-zinc-500">Name:</span> <span className="font-medium">{part.part_name}</span></div>
                            <div><span className="text-zinc-500">No.:</span> <span className="font-mono">{part.part_number || "—"}</span></div>
                            <div><span className="text-zinc-500">Status:</span> <span>{part.status || "—"}</span></div>
                        </div>
                    </div>

                    <div className="border border-zinc-200 p-6 col-span-2 space-y-4">
                        <div>
                            <div className="tag-pill mb-2">Cost Buildup</div>
                            <h3 className="text-lg font-bold">Quotation basis (per piece)</h3>
                        </div>
                        <table className="w-full sheet-table">
                            <thead><tr><th>Component</th><th className="text-right">Rs / piece</th><th className="text-right">% of price</th></tr></thead>
                            <tbody>
                                {[
                                    ["Material", totals.material_total],
                                    ["Process (in-house)", totals.process_total],
                                    ["Outsource", totals.outsource_total],
                                    ["Investment (amortised)", totals.amortised_investment_per_piece],
                                    ["Packing", totals.packing],
                                    ["Transportation", totals.transportation],
                                    ["ICC", totals.icc],
                                    ["SGA", totals.sga],
                                    ["Profit", totals.profit_value],
                                ].map(([label, value]) => (
                                    <tr key={label}>
                                        <td>{label}</td>
                                        <td className="text-right font-mono">₹ {value.toFixed(2)}</td>
                                        <td className="text-right font-mono text-zinc-500">{derivedPrice ? ((value / derivedPrice) * 100).toFixed(1) : "0.0"}%</td>
                                    </tr>
                                ))}
                                <tr className="font-semibold bg-zinc-50">
                                    <td>Derived Price</td>
                                    <td className="text-right font-mono">₹ {derivedPrice.toFixed(2)}</td>
                                    <td className="text-right font-mono">100.0%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Charts row 1: waterfall + annual */}
                <div className="grid grid-cols-2 gap-6">
                    <div className="border border-zinc-200 p-6">
                        <div className="tag-pill mb-3">Per-Piece Breakup</div>
                        <h3 className="text-xl font-bold tracking-tight mb-1">Waterfall to derived price</h3>
                        <p className="text-xs text-zinc-500 mb-4 font-mono">Each step adds to the price / piece</p>
                        <div style={{ height: 280 }}>
                            <ResponsiveContainer>
                                <BarChart data={waterfallData} margin={{ top: 16, right: 8, left: 8, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v}`} />
                                    <Tooltip
                                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                                        formatter={(val, name, ctx) => {
                                            if (name === "base") return null;
                                            const row = ctx?.payload;
                                            return [`₹ ${Number(val).toFixed(2)}`, row?.isTotal ? "Derived Price" : "Adds"];
                                        }}
                                    />
                                    <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
                                    <Bar dataKey="value" stackId="w" isAnimationActive={false}>
                                        {waterfallData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-4 space-y-1.5">
                            {waterfallData.filter((d) => !d.isTotal).map((d) => {
                                const total = waterfallData.find((x) => x.isTotal)?.value || 1;
                                const pct = (d.value / total) * 100;
                                return (
                                    <div key={d.name} className="flex items-center gap-3 text-xs font-mono">
                                        <div className="w-3 h-3" style={{ background: d.color }} />
                                        <div className="flex-1">{d.name}</div>
                                        <div className="text-zinc-500">₹ {d.value.toFixed(2)}</div>
                                        <div className="font-semibold w-14 text-right">{pct.toFixed(1)}%</div>
                                    </div>
                                );
                            })}
                            <div className="flex items-center gap-3 text-xs font-mono pt-2 border-t border-zinc-200 mt-2">
                                <div className="w-3 h-3 bg-[#09090b]" />
                                <div className="flex-1 font-semibold">Derived Price / piece</div>
                                <div className="font-bold">₹ {(waterfallData.find((x) => x.isTotal)?.value || 0).toFixed(2)}</div>
                            </div>
                        </div>
                    </div>
                    <div className="border border-zinc-200 p-6">
                        <div className="tag-pill mb-3">Annual Impact</div>
                        <h3 className="text-xl font-bold tracking-tight mb-4">Revenue · Cost · Profit</h3>
                        <div style={{ height: 260 }}>
                            <ResponsiveContainer>
                                <BarChart data={annualData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v.toLocaleString()} />
                                    <Tooltip formatter={(v) => `₹ ${Number(v).toLocaleString()}`} />
                                    <Bar dataKey="value">
                                        {annualData.map((d, i) => (
                                            <Cell key={i} fill={i === 0 ? "#002FA7" : i === 1 ? "#E63946" : "#2A9D8F"} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Yearly projection charts */}
                <div className="grid grid-cols-2 gap-6">
                    <div className="border border-zinc-200 p-6">
                        <div className="tag-pill mb-3">Yearly Projection</div>
                        <h3 className="text-xl font-bold tracking-tight mb-1">Revenue &amp; Profit by Year</h3>
                        <p className="text-xs text-zinc-500 mb-4 font-mono">Project life: {totals.project_years} year{totals.project_years === 1 ? "" : "s"} · Edit yearly volumes below to update</p>
                        <div style={{ height: 260 }}>
                            <ResponsiveContainer>
                                <BarChart data={projection}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                    <XAxis dataKey="year" tick={{ fontSize: 11 }} tickFormatter={(v) => `Y${v}`} />
                                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v.toLocaleString()} />
                                    <Tooltip formatter={(v) => `₹ ${Number(v).toLocaleString()}`} labelFormatter={(l) => `Year ${l}`} />
                                    <Legend />
                                    <Bar dataKey="revenue" name="Revenue" fill="#002FA7" />
                                    <Bar dataKey="profit" name="Profit" fill="#2A9D8F" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className="border border-zinc-200 p-6">
                        <div className="tag-pill mb-3">Cumulative</div>
                        <h3 className="text-xl font-bold tracking-tight mb-1">Cumulative Revenue &amp; Profit</h3>
                        <p className="text-xs text-zinc-500 mb-4 font-mono">Running totals over project life</p>
                        <div style={{ height: 260 }}>
                            <ResponsiveContainer>
                                <LineChart data={projection}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                    <XAxis dataKey="year" tick={{ fontSize: 11 }} tickFormatter={(v) => `Y${v}`} />
                                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v.toLocaleString()} />
                                    <Tooltip formatter={(v) => `₹ ${Number(v).toLocaleString()}`} labelFormatter={(l) => `Year ${l}`} />
                                    <Legend />
                                    <Line type="monotone" dataKey="cumulative_revenue" name="Cum. Revenue" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="cumulative_profit" name="Cum. Profit" stroke="#2A9D8F" strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Editable yearly volume table */}
                <div className="border border-zinc-200">
                    <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200 gap-3 flex-wrap">
                        <div>
                            <div className="text-sm font-semibold tracking-tight">Year-by-Year (editable volume)</div>
                            <div className="text-[11px] text-zinc-500 font-mono">Override volume per year if you expect ramp-up / down. Empty rows default to annual volume.</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" className="rounded-none h-8" onClick={resetYearlyToAnnual} data-testid="reset-yearly-volumes">Reset to annual</Button>
                            <Button className="rounded-none h-8 bg-[#E63946] hover:bg-[#C92836]" onClick={saveYearlyVolumes} disabled={savingVol} data-testid="save-yearly-volumes">
                                <SaveIcon size={14} className="mr-2" /> {savingVol ? "Saving…" : "Save & recalc"}
                            </Button>
                        </div>
                    </div>
                    <div className="p-4">
                        <table className="w-full sheet-table" data-testid="proposal-projection-table">
                            <thead>
                                <tr>
                                    <th>Year</th>
                                    <th>Volume (editable)</th>
                                    <th className="text-right">Revenue (₹)</th>
                                    <th className="text-right">Profit (₹)</th>
                                    <th className="text-right">Cum. Revenue (₹)</th>
                                    <th className="text-right">Cum. Profit (₹)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(projection || []).map((p, idx) => {
                                    const editVal = editVolumes[idx] ?? p.volume ?? part.annual_volume;
                                    return (
                                        <tr key={p.year}>
                                            <td className="font-mono">Y{p.year}</td>
                                            <td>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={editVal}
                                                    onChange={(e) => {
                                                        const next = [...editVolumes];
                                                        next[idx] = e.target.value;
                                                        setEditVolumes(next);
                                                    }}
                                                    className="h-8 rounded-none font-mono w-32"
                                                    data-testid={`yearly-volume-${p.year}`}
                                                />
                                            </td>
                                            <td className="text-right font-mono">{p.revenue.toLocaleString()}</td>
                                            <td className="text-right font-mono">{p.profit.toLocaleString()}</td>
                                            <td className="text-right font-mono">{p.cumulative_revenue.toLocaleString()}</td>
                                            <td className="text-right font-mono">{p.cumulative_profit.toLocaleString()}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Terms */}
                <div className="border border-zinc-200 p-6 space-y-3 text-sm">
                    <div className="tag-pill mb-2">Terms (standard)</div>
                    <ul className="list-disc pl-5 space-y-1 text-zinc-700">
                        <li>Validity: 30 days from date of submission.</li>
                        <li>Payment terms: 60 days net from delivery.</li>
                        <li>Volume basis: Annual commitment of {(part.annual_volume || 0).toLocaleString()} pieces.</li>
                        <li>Project life: {totals.project_years} years (used for investment amortisation).</li>
                        <li>Price excludes applicable taxes and duties.</li>
                        <li>Tool / fixture investment {totals.total_investment > 0 ? `(₹ ${totals.total_investment.toLocaleString()})` : ""} is {totals.amortised_investment_per_piece > 0 ? "amortised into the price" : "billed separately"}.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
